import { supabase } from "@/integrations/supabase/client";
import { ActivityLogger } from "./activity-logger";

interface InsertSelectBuilder<T> extends PromiseLike<{ data: T[] | null; error: unknown }> {
  single(): Promise<{ data: T | null; error: unknown }>;
}

interface InsertBuilder<T> {
  select(): InsertSelectBuilder<T>;
}

type InsertClient = {
  from: <T>(table: string) => {
    insert(values: unknown): InsertBuilder<T>;
  };
};

const insertClient = supabase as unknown as InsertClient;

export interface PolicyTemplate {
  id: string;
  title: string;
  description: string | null;
  content: string;
  template_type: 'global' | 'internal';
  category: string | null;
  department: string | null;
  tags: string[];
  compliance_framework: string | null;
  control_ids?: string[];
  customization_prompts?: unknown[];
  company_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  usage_count: number;
  is_active: boolean;
}

export interface TemplateStack {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  category: string;
  compliance_framework: string | null;
  templates: string[]; // Array of template IDs
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Copy a template to create a new policy as a draft
 */
export async function copyTemplateToPolicy(
  templateId: string,
  companyId: string,
  userId: string
): Promise<string | null> {
  try {
    // Fetch the template
    const { data: template, error: templateError } = await supabase
      .from('policy_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (templateError) throw templateError;
    if (!template) throw new Error('Template not found');

    // Check if template is accessible
    if (template.template_type === 'internal' && template.company_id !== companyId) {
      throw new Error('Template not accessible');
    }

    // Create the policy from template
    const { data: policy, error: policyError } = await insertClient
      .from<{ id: string; title: string }>('policies')
      .insert({
        title: template.title,
        content: template.content,
        description: template.description,
        department: template.department || 'general',
        status: 'draft',
        created_by: userId,
        company_id: companyId,
        source_template_id: template.id,
        customization_fields: { prompts: template.customization_prompts || [] },
      })
      .select()
      .single();

    if (policyError) throw policyError;

    // Increment template usage count (only for internal templates)
    if (template.template_type === 'internal') {
      await supabase.rpc('increment_template_usage', {
        template_id: templateId,
      });
    }

    // Log activity
    await ActivityLogger.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'policy_created_from_template',
      resource_type: 'policy',
      resource_id: policy.id,
      resource_title: policy.title,
      details: { template_id: templateId, template_title: template.title }
    });

    return policy.id;
  } catch (error) {
    console.error('Error copying template to policy:', error);
    throw error;
  }
}

/**
 * Create multiple policies from a template stack
 */
export async function createPoliciesFromStack(
  stackId: string,
  companyId: string,
  userId: string,
  selectedTemplateIds: string[] = []
): Promise<string[]> {
  try {
    // Fetch the stack
    const { data: stack, error: stackError } = await supabase
      .from('template_stacks')
      .select('*')
      .eq('id', stackId)
      .single();

    if (stackError) throw stackError;
    if (!stack) throw new Error('Stack not found');

    // If no specific templates selected, use all templates in stack
    const templatesToCreate = selectedTemplateIds.length > 0 
      ? selectedTemplateIds 
      : stack.templates;

    if (templatesToCreate.length === 0) {
      throw new Error('No templates in stack');
    }

    // Fetch all templates
    const { data: templates, error: templatesError } = await supabase
      .from('policy_templates')
      .select('*')
      .in('id', templatesToCreate)
      .eq('is_active', true);

    if (templatesError) throw templatesError;
    if (!templates || templates.length === 0) {
      throw new Error('No templates found');
    }

    // Create all policies from templates
    const policies = templates.map(template => ({
      title: template.title,
      content: template.content,
      description: template.description,
      department: template.department || 'general',
      status: 'draft' as const,
      created_by: userId,
      company_id: companyId,
      source_template_id: template.id,
      customization_fields: { prompts: template.customization_prompts || [] },
    }));

    const { data: createdPolicies, error: insertError } = await insertClient
      .from<{ id: string }>('policies')
      .insert(policies)
      .select();

    if (insertError) throw insertError;

    // Increment usage count for internal templates
    const internalTemplateIds = templates
      .filter(t => t.template_type === 'internal')
      .map(t => t.id);

    if (internalTemplateIds.length > 0) {
      // Bulk increment usage counts
      await Promise.all(
        internalTemplateIds.map(id =>
          supabase.rpc('increment_template_usage', { template_id: id })
        )
      );
    }

    // Log activities for all created policies
    await Promise.all(
      (createdPolicies || []).map(policy =>
        ActivityLogger.logActivity({
          company_id: companyId,
          user_id: userId,
          action_type: 'policy_created_from_stack',
          resource_type: 'policy',
          resource_id: policy.id,
          resource_title: policy.title,
          details: { stack_id: stackId, stack_name: stack.name }
        })
      )
    );

    return (createdPolicies || []).map(p => p.id);
  } catch (error) {
    console.error('Error creating policies from stack:', error);
    throw error;
  }
}

/**
 * Convert an existing policy to an internal template
 */
export async function convertPolicyToTemplate(
  policyId: string,
  companyId: string,
  userId: string,
  templateData: {
    title: string;
    description?: string;
    category?: string;
    department?: string;
    tags?: string[];
  }
): Promise<string> {
  try {
    // Fetch the policy
    const { data: policy, error: policyError } = await supabase
      .from('policies')
      .select('*')
      .eq('id', policyId)
      .eq('company_id', companyId)
      .single();

    if (policyError) throw policyError;
    if (!policy) throw new Error('Policy not found');

    // Create the template
    const { data: template, error: templateError } = await supabase
      .from('policy_templates')
      .insert({
        title: templateData.title,
        description: templateData.description || policy.description || null,
        content: policy.content,
        template_type: 'internal',
        category: templateData.category || null,
        department: templateData.department || policy.department || null,
        tags: templateData.tags || [],
        company_id: companyId,
        created_by: userId,
      })
      .select()
      .single();

    if (templateError) throw templateError;

    // Log activity
    await ActivityLogger.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'template_created_from_policy',
      resource_type: 'policy_template',
      resource_id: template.id,
      resource_title: template.title,
      details: { policy_id: policyId, policy_title: policy.title }
    });

    return template.id;
  } catch (error) {
    console.error('Error converting policy to template:', error);
    throw error;
  }
}

/**
 * Get template with full details
 */
export async function getTemplate(templateId: string): Promise<PolicyTemplate | null> {
  try {
    const { data, error } = await supabase
      .from('policy_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (error) throw error;
    return data as PolicyTemplate;
  } catch (error) {
    console.error('Error fetching template:', error);
    return null;
  }
}

/**
 * Get all templates for a stack
 */
export async function getStackTemplates(stack: TemplateStack): Promise<PolicyTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('policy_templates')
      .select('*')
      .in('id', stack.templates)
      .eq('is_active', true);

    if (error) throw error;
    return (data || []) as PolicyTemplate[];
  } catch (error) {
    console.error('Error fetching stack templates:', error);
    return [];
  }
}
